import "./UserProfile.css";
import { Icon, Logo, Tooltip } from "@saviynt/design-system";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import type { Tenant } from "../TenantSwitcher";

const MAX_TENANT_NAME_LEN = 20;

interface UserProfileProps {
  onLogout: () => void;
  userName?: string;
  theme?: "light" | "dark";
  onToggleTheme?: () => void;
  tenants?: Tenant[];
  currentTenantId?: string;
  onTenantSelect?: (tenantId: string) => void;
  isSwitchingTenant?: boolean;
}

function MenuRow({
  children,
  onClick,
  disabled,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2 w-full px-3 py-2.5 text-left rounded-md
        hover:bg-[var(--color-background-neutral-subtle,rgba(55,81,255,0.06))]
        active:bg-[var(--color-background-neutral-subtle,rgba(55,81,255,0.1))]
        disabled:cursor-not-allowed disabled:opacity-60
        transition-colors duration-100 outline-none
        focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus,#3751ff)]
        ${className}`}
    >
      {children}
    </button>
  );
}

/** Sun icon with exact #FFBE00 fill — used for the "Light mode" toggle row. */
function SunIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      data-testid="icon-sun"
      className="shrink-0"
    >
      <circle cx="12" cy="12" r="5" fill="#FFBE00" />
      {(
        [
          ["12", "1", "12", "3"],
          ["12", "21", "12", "23"],
          ["1", "12", "3", "12"],
          ["21", "12", "23", "12"],
          ["4.22", "4.22", "5.64", "5.64"],
          ["18.36", "18.36", "19.78", "19.78"],
          ["4.22", "19.78", "5.64", "18.36"],
          ["18.36", "5.64", "19.78", "4.22"],
        ] as [string, string, string, string][]
      ).map(([x1, y1, x2, y2], i) => (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#FFBE00"
          strokeWidth="2"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** Moon/crescent icon with exact #5B7CF6 fill — used for the "Dark mode" toggle row. */
function MoonIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      data-testid="icon-moon"
      className="shrink-0"
    >
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        fill="#5B7CF6"
      />
    </svg>
  );
}

/**
 * EnterpriseRole fills the 20 × 20 container; Exchange is a ≈10 px badge at
 * the bottom-right corner on a static white circle so it reads in both themes.
 */
function TenantSwitchIcon() {
  return (
    <span aria-hidden className="relative inline-flex shrink-0 w-5 h-5">
      <span className="up-icon leading-[0]">
        <Icon kind="EnterpriseRole" size="small" />
      </span>
      <span
        className="absolute bottom-0.5 right-0.5 origin-bottom-right scale-[0.65]
          rounded-full p-px leading-[0] bg-white"
      >
        <Icon kind="Exchange" size="small" />
      </span>
    </span>
  );
}

function Divider() {
  return (
    <div
      className="my-1 border-t border-[var(--color-border-neutral-subtlest,#ebecf2)]"
      role="separator"
      aria-hidden
    />
  );
}

/**
 * Full-page cover rendered via portal while a tenant switch is in progress.
 * The Saviynt logo flips on its Y-axis in the centre; the target tenant name
 * is shown below.
 */
function TenantSwitchCover({ tenantName }: { tenantName: string }) {
  const intl = useIntl();

  const label = intl.formatMessage(
    {
      id: "dhAz/E",
      defaultMessage: "Switching to {name}",
      description: "Full-page cover label while a tenant switch is in progress",
    },
    { name: tenantName },
  );
  const displayText = `${label}…`;

  return createPortal(
    <div
      data-testid="tenant-switch-cover"
      role="status"
      aria-live="polite"
      aria-label={displayText}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-16
        bg-black/50 backdrop-blur-sm"
    >
      <span className="up-cover-icon-wrap">
        <span className="up-cover-flip inline-flex">
          <Logo kind="Ispm" label="Ispm Logo" className="up-cover-logo" />
        </span>
      </span>
      <p className="text-xl font-medium text-white">{displayText}</p>
    </div>,
    document.body,
  );
}

function TenantName({ name, isActive }: { name: string; isActive: boolean }) {
  const truncated = name.length > MAX_TENANT_NAME_LEN;
  const display = truncated ? `${name.slice(0, MAX_TENANT_NAME_LEN)}…` : name;
  const colorClass = isActive
    ? "font-medium text-[var(--color-foreground-primary-bold,#3751ff)]"
    : "text-[var(--color-foreground-neutral-bold)]";

  const label = (
    <span className={`flex-1 truncate text-sm cursor-default ${colorClass}`}>
      {display}
    </span>
  );

  if (!truncated) return label;

  return <Tooltip trigger={label} text={name} placement="top" />;
}

export function UserProfile({
  onLogout,
  userName,
  theme,
  onToggleTheme,
  tenants,
  currentTenantId,
  onTenantSelect,
  isSwitchingTenant = false,
}: UserProfileProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [tenantExpanded, setTenantExpanded] = useState(false);
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);

  useEffect(() => {
    if (!isSwitchingTenant) setPendingTenantId(null);
  }, [isSwitchingTenant]);

  const closeMenu = () => {
    setOpen(false);
    setTenantExpanded(false);
  };

  const showTenantRow =
    tenants != null &&
    tenants.length > 1 &&
    currentTenantId != null &&
    onTenantSelect != null;

  const ariaLabel = intl.formatMessage({
    id: "gC5t78",
    defaultMessage: "User profile menu",
    description: "User profile menu trigger aria label",
  });

  const switchTenantLabel = intl.formatMessage({
    id: "ZIoPeo",
    defaultMessage: "Switch Tenant",
    description: "Switch tenant menu item label",
  });

  const darkModeLabel = intl.formatMessage({
    id: "hMUQH5",
    defaultMessage: "Dark mode",
    description: "Theme switcher label",
  });

  const lightModeLabel = intl.formatMessage({
    id: "Q7dLbA",
    defaultMessage: "Light mode",
    description: "Theme switcher label",
  });

  const logOutLabel = intl.formatMessage({
    id: "H0JBH6",
    defaultMessage: "Log Out",
    description: "Log out menu item",
  });

  const pendingTenantName =
    pendingTenantId != null
      ? (tenants?.find((t) => t.tenantId === pendingTenantId)?.name ??
        pendingTenantId)
      : null;

  const isSwitchingWithPending = isSwitchingTenant && pendingTenantName != null;

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          setOpen((p) => !p);
          setTenantExpanded(false);
        }}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-2 rounded-md px-2 py-1.5
          hover:bg-[var(--color-background-neutral-subtle,rgba(55,81,255,0.06))]
          transition-colors outline-none
          focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus,#3751ff)]"
      >
        <span
          className="up-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full
            border border-[var(--color-border-neutral-medium,#c5c9d6)]"
        >
          <Icon kind="User" size="small" />
        </span>
        {userName && (
          <span className="text-sm font-medium text-[var(--color-foreground-neutral-bold)]">
            {userName}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={closeMenu} />
          <div
            role="menu"
            className="absolute right-0 top-full mt-1 z-30 min-w-[220px] py-1
              bg-[var(--color-background-neutral-subtlest,#fff)]
              rounded-lg border border-[var(--color-border-neutral-subtlest,#ebecf2)]
              shadow-[var(--box-shadow-overlay-interaction)]"
          >
            {/* Switch Tenant row */}
            {showTenantRow && (
              <>
                <MenuRow onClick={() => setTenantExpanded((p) => !p)}>
                  <TenantSwitchIcon />
                  <span className="flex-1 text-sm text-[var(--color-foreground-neutral-bold)]">
                    {switchTenantLabel}
                  </span>
                  <span className="up-icon shrink-0">
                    <Icon
                      kind={tenantExpanded ? "ChevronUp" : "ChevronDown"}
                      size="small"
                    />
                  </span>
                </MenuRow>

                {tenantExpanded && (
                  <div
                    role="listbox"
                    aria-label={switchTenantLabel}
                    className="pb-1"
                  >
                    {tenants!.map((tenant) => {
                      const isActive = tenant.tenantId === currentTenantId;
                      return (
                        <button
                          key={tenant.tenantId}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          disabled={isActive || isSwitchingTenant}
                          onClick={() => {
                            if (isActive || isSwitchingTenant) return;
                            setPendingTenantId(tenant.tenantId);
                            closeMenu();
                            onTenantSelect!(tenant.tenantId);
                          }}
                          className={`flex items-center gap-2 w-full pl-9 pr-3 py-2 text-left outline-none
                            transition-colors duration-100
                            ${
                              isActive
                                ? "cursor-default"
                                : "hover:bg-[var(--color-background-neutral-subtle,rgba(55,81,255,0.06))] cursor-pointer"
                            }`}
                        >
                          {isSwitchingTenant && !isActive ? (
                            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--color-border-primary-bold,#3751ff)] border-t-transparent" />
                          ) : (
                            <span className="up-icon shrink-0">
                              <Icon kind="EnterpriseRole" size="small" />
                            </span>
                          )}
                          <TenantName name={tenant.name} isActive={isActive} />
                          {isActive && (
                            <span className="up-icon shrink-0">
                              <Icon kind="Check" size="small" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Dark / Light mode */}
            {theme != null && onToggleTheme && (
              <MenuRow
                onClick={() => {
                  onToggleTheme();
                  closeMenu();
                }}
              >
                {theme === "light" ? <MoonIcon /> : <SunIcon />}
                <span className="flex-1 text-sm text-[var(--color-foreground-neutral-bold)]">
                  {theme === "light" ? darkModeLabel : lightModeLabel}
                </span>
              </MenuRow>
            )}

            <Divider />

            {/* Log Out */}
            <MenuRow
              onClick={() => {
                closeMenu();
                onLogout();
              }}
            >
              <span className="up-icon flex shrink-0 items-center justify-center w-5 h-5">
                <Icon kind="Logout" size="small" />
              </span>
              <span className="flex-1 text-sm text-[var(--color-foreground-critical-bold,#dc2626)]">
                {logOutLabel}
              </span>
            </MenuRow>
          </div>
        </>
      )}

      {/* Full-page switching cover — portalled to document.body */}
      {isSwitchingWithPending && (
        <TenantSwitchCover tenantName={pendingTenantName!} />
      )}
    </div>
  );
}
